import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, Minus, Plus, Search, SlidersHorizontal } from 'lucide-react';
import {
  invalidateJobRoles,
  selectFiltersAvailable,
  subSubTabsFor,
  subTabsFor,
  SUBTAB_LABELS,
  updateActiveSubSubTab,
  updateActiveSubTab,
  updateActiveTab,
  updatePage,
  updateSearchQuery,
  updateSelectedJob,
  updateSelectedSearch,
} from '../store/slices/findCandidatesSlice';
import FindCandidatesFilter from './shared_components/FindCandidatesFilter';
import FilterModal from '../sourcing/components/FilterModal';
import { getJobSearchesThunk, getSourcingJobsThunk } from '../store/thunks/FindCandidatesThunk';

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const TABS = [
  { key: 'SOURCING', label: 'Sourcing' },
  { key: 'OUTREACH', label: 'Outreach' },
];

/** A search names itself by its alias, or by the query that was typed. Native <option>
 *  can't ellipsise, so long queries are cut here rather than stretching the control. */
const searchLabel = (s) => {
  const text = s?.alias || s?.raw_query || 'Untitled search';
  return text.length > 48 ? `${text.slice(0, 48)}…` : text;
};

/**
 * Header for Find Candidates — the same shape as CandidateManagementHeader: a job-role
 * dropdown, a row of section tabs, then a pill bar of sub-tabs. The dropdown is how you
 * switch scope without going back to the picker screen, so it offers the same set of roles
 * that screen does: every job role the recruiter has, sourced under or not.
 */
function FindCandidatesHeader({ portal = 'org-admin', onRefetch }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [showDropDown, setShowDropDown] = useState(false);
  const [jobSearch, setJobSearch] = useState('');
  const [editFiltersOpen, setEditFiltersOpen] = useState(false);
  const [editRunning, setEditRunning] = useState(false);
  const [editError, setEditError] = useState(null);
  // Why the last Edit Filters run found nobody — shown in the modal, which stays open for it.
  const [editNotice, setEditNotice] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const activeTab = useSelector((state) => state.findCandidatesSlice.activeTab);
  const activeSubTab = useSelector((state) => state.findCandidatesSlice.activeSubTab);
  const activeSubSubTab = useSelector((state) => state.findCandidatesSlice.activeSubSubTab);
  const selectedJobId = useSelector((state) => state.findCandidatesSlice.selectedJobId);
  const selectedJobRole = useSelector((state) => state.findCandidatesSlice.selectedJobRole);
  const jobRoles = useSelector((state) => state.findCandidatesSlice.jobRoles);
  const searchQuery = useSelector((state) => state.findCandidatesSlice.searchQuery);
  const searches = useSelector((state) => state.findCandidatesSlice.searches);
  const selectedSearchId = useSelector((state) => state.findCandidatesSlice.selectedSearchId);
  const currentPage = useSelector((state) => state.findCandidatesSlice.currentPage);

  useEffect(() => {
    // No-ops when the picker has already loaded the roles — the thunk's `condition` guard
    // skips the fetch. The header only needs the role labels for its dropdown, so a cached
    // list is fine; the picker is what forces a refresh.
    dispatch(getSourcingJobsThunk());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropDown(false);
        setJobSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const visibleJobRoles = (jobRoles || []).filter((j) =>
    (j.job_role || '').toLowerCase().includes(jobSearch.toLowerCase()),
  );

  const selectedSearch = (searches || []).find((s) => s.search_id === selectedSearchId) || null;

  const statusOptions = subSubTabsFor(activeTab, activeSubTab);
  const filtersAvailable = useSelector(selectFiltersAvailable);

  // The job scope lives in the URL, so switching roles is a navigation. The store is set
  // here too rather than left to the route effect, so the dropdown reads as the new role
  // immediately instead of flickering through the old one.
  const selectJob = (jobId, jobRole) => {
    dispatch(updateSelectedJob({ jobId, jobRole }));
    setShowDropDown(false);
    setJobSearch('');
    navigate(`/${portal}/find-candidates/${jobId}`, { state: { job_role: jobRole } });
  };

  // Editing filters means re-running one search in place, so it needs a search in scope —
  // "All Searches" has no single filter set to edit. The parsed query rides along on the
  // dropdown's own options, so the modal opens without an extra fetch.
  const editableSearch = selectedSearchId && selectedSearch?.parsed_query ? selectedSearch : null;

  /* CrustData knows how many people matched in all and hands back a cursor for the page after
     the one we took. Load more runs that next page with the same filters and appends it to the
     search, so the pool size is a fact the recruiter can act on rather than a mystery. */
  const runLoadMore = async () => {
    if (!selectedSearch?.next_cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      // Paging replays exactly what the search sent — the provider refuses a cursor against
      // any other request — so this carries nothing but the search's id.
      await axios.post(`${SOURCING_URL}/sourcing/find`, {
        query: selectedSearch.raw_query || '',
        search_id: selectedSearchId,
        load_more: true,
        job_id: selectedJobId,
        job_role: selectedJobRole,
      });
      dispatch(getJobSearchesThunk({ jobId: selectedJobId }));
      dispatch(invalidateJobRoles());
      if (currentPage === 1) onRefetch?.();
      else dispatch(updatePage(1));
    } catch (err) {
      setEditError(
        err.response?.data?.detail || "Couldn't load more candidates. Please try again.",
      );
    } finally {
      setLoadingMore(false);
    }
  };

  /* Same call the results screen's Edit Filters makes: /sourcing/find with this search's id
     updates that record rather than creating another, and the backend keeps the candidates
     already acted on. Then the list — and the dropdown's result counts — are reloaded. */
  const runEditedFilters = async (updatedQuery) => {
    if (!editableSearch || editRunning) return;
    setEditRunning(true);
    setEditError(null);
    setEditNotice(null);
    try {
      const { data } = await axios.post(`${SOURCING_URL}/sourcing/find`, {
        query: editableSearch.raw_query || '',
        canonical_query: updatedQuery,
        search_id: selectedSearchId,
        job_id: selectedJobId,
        job_role: selectedJobRole,
      });
      dispatch(getJobSearchesThunk({ jobId: selectedJobId }));
      dispatch(invalidateJobRoles());
      // Row one is where the new matches are. Off page one the page change refetches on
      // its own — calling both would race two requests for different pages.
      if (currentPage === 1) onRefetch?.();
      else dispatch(updatePage(1));
      // Nobody new matched, or fewer than a page did: keep the modal open with the reason,
      // so the next edit starts from what was just tried.
      const shortfall = data?.no_results_hint?.message || data?.notice?.message;
      if (shortfall) setEditNotice(shortfall);
      else setEditFiltersOpen(false);
    } catch (err) {
      setEditError(
        err.response?.data?.detail || 'Search failed. Please adjust the filters and try again.',
      );
    } finally {
      setEditRunning(false);
    }
  };

  return (
    <div className="relative flex flex-col w-full">
      <style>{`
        .fc-tab-active { z-index: 10; }
        .fc-tab-active::before {
          content: ''; position: absolute; bottom: 0; left: -12px; width: 12px; height: 12px;
          background: radial-gradient(circle at -0% -0%, #FAFAFA 64%, #d1d5db 73%, #d1d5db 20%, white 72%);
        }
        .fc-tab-active::after {
          content: ''; position: absolute; bottom: 0; right: -12px; width: 12px; height: 12px;
          background: radial-gradient(circle at 100% -0%, #FAFAFA 64%, #d1d5db 70%, #d1d5db 20%, white 72%);
        }
        .dark .fc-tab-active::before {
          background: radial-gradient(circle at -0% -0%, transparent 64%, #374151 70%, #374151 72%, #1e1b4b 75%);
        }
        .dark .fc-tab-active::after {
          background: radial-gradient(circle at 100% -0%, transparent 64%, #374151 70%, #374151 72%, #1e1b4b 75%);
        }
      `}</style>

      {/* Job scope + primary action */}
      <div className="flex flex-row items-center justify-between mt-6 mb-3">
        <div className="relative inline-block" ref={dropdownRef}>
          {/* Labelled outline (same treatment as the job-creation form): the legend
              punches the gap in the border itself, so it sits correctly on any page
              background in both themes. Focus shifts the border colour rather than
              adding a ring — a ring follows the border-box and would paint a line
              straight through the legend text, which straddles that edge. */}
          <fieldset
            className="min-w-0 cursor-pointer rounded-3xl border border-[#5A4479] px-4 pb-2.5 transition-colors duration-200 focus-within:border-purple-500"
            onClick={() => setShowDropDown(true)}
          >
            <legend className="ml-2 px-1.5 text-sm font-normal text-textSecondary dark:text-[#DAC0FF]">
              Job Role
            </legend>
            <div className="flex items-center min-w-0">
              <input
                type="text"
                value={showDropDown ? jobSearch : selectedJobRole || ''}
                onChange={(e) => {
                  setJobSearch(e.target.value);
                  setShowDropDown(true);
                }}
                onFocus={() => {
                  setJobSearch('');
                  setShowDropDown(true);
                }}
                placeholder={jobSearch || 'Select Job Role'}
                title={selectedJobRole || 'Select Job Role'}
                className="text-textSecondary dark:text-[#CCCCCC] text-base max-w-[180px] truncate bg-transparent outline-none border-none ring-0 focus:outline-none focus:ring-0 cursor-pointer min-w-0 placeholder-textSecondary font-medium"
              />
              <ChevronDown className="size-4 ml-2 flex-shrink-0" />
            </div>
          </fieldset>

          {showDropDown && (
            <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
              <div className="max-h-52 overflow-y-auto">
                {visibleJobRoles.map((job) => (
                  <div
                    key={job.job_id}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                    onClick={() => selectJob(job.job_id, job.job_role || 'Untitled role')}
                  >
                    {job.job_role || 'Untitled role'}
                  </div>
                ))}
                {visibleJobRoles.length === 0 && (
                  <div className="px-4 py-3 text-sm text-gray-400 text-center">
                    {jobSearch ? 'No results found' : 'No job roles yet'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Refines the search in scope. Disabled rather than hidden under "All Searches":
              the action still applies to this screen, it just needs one search chosen —
              hiding it would read as the feature being unavailable for the job role. */}
          <button
            onClick={() => {
              setEditError(null);
              setEditFiltersOpen(true);
            }}
            disabled={!editableSearch}
            title={
              editableSearch
                ? `Edit filters for “${searchLabel(editableSearch)}”`
                : 'Select a single search to edit its filters'
            }
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium border border-gray-300 dark:border-gray-600 text-textSecondary dark:text-gray-300 transition-colors enabled:hover:border-purple-400 enabled:hover:text-purple-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <SlidersHorizontal className="w-4 h-4" /> Edit Filters
          </button>

          {/* Carry the job in scope over to the search screen, the same way ProjectDetail
              does, so the recruiter doesn't re-pick the role they just came from. */}
          <button
            onClick={() =>
              navigate(`/${portal}/sourcing`, {
                state: selectedJobId ? { job_id: selectedJobId, job_role: selectedJobRole } : undefined,
              })
            }
            className="flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium text-white hover:opacity-90"
            style={{ background: 'linear-gradient(to right, #ec4899, #3b82f6)' }}
          >
            <Plus className="w-4 h-4" /> New Search
          </button>
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex pb-0 mt-4 items-center justify-between space-x-4 dark:border-[#5A4479A3] border-gray-300 mb-[-1px]">
        <div className="flex items-center">
          {TABS.map((tab) => (
            <div
              key={tab.key}
              onClick={() => dispatch(updateActiveTab(tab.key))}
              className={`px-3 mx-0.5 cursor-pointer relative ${
                activeTab === tab.key
                  ? 'bg-white py-3 dark:bg-indigo-950 dark:border-gray-700 fc-tab-active rounded-t-[11px] border-t-[1px] border-l-[1px] border-r-[1px]'
                  : ''
              }`}
            >
              <span
                className={`block relative
                  ${
                    activeTab === tab.key
                      ? 'font-semibold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent'
                      : 'hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent hover:scale-[103%]'
                  }
                  before:block before:font-semibold before:invisible before:h-0 before:overflow-hidden before:content-[attr(data-text)]
                `}
                data-text={tab.label}
              >
                {tab.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Sub-tab pills + search */}
      <div className="relative flex space-x-4 !bg-bgPrimary dark:!bg-indigo-950 items-center px-10 dark:border-gray-700 w-[calc(100%+4rem)] !overflow-x-hidden -ml-8 -mr-8 py-4 border">
        <div className="w-full flex items-center space-x-4 flex-wrap gap-y-2">
          {subTabsFor(activeTab).map((subTab) => (
            <button
              key={subTab}
              onClick={() => dispatch(updateActiveSubTab(subTab))}
              className={`${
                activeSubTab === subTab
                  ? 'button !text-xs text-textSecondary dark:!text-white'
                  : 'py-2 rounded-full border border-gray-300'
              } !px-5 !py-2 text-xs`}
            >
              <span
                className={`${
                  activeSubTab !== subTab
                    ? 'text-textSecondary hover:bg-gradient-to-r hover:from-pink-500 hover:to-blue-500 hover:bg-clip-text hover:text-transparent'
                    : 'text-white'
                } text-xs`}
              >
                {SUBTAB_LABELS[subTab]}
              </span>
            </button>
          ))}

          {/* Search scope. Every list endpoint takes a search_id, so this refines Sourcing
              and Outreach alike — unlike the filter popover, which is Sourcing-only. */}
          {(searches.length > 0 || selectedSearchId) && (
            <div className="flex flex-row items-center justify-start">
              <Minus className="text-gray-300 rotate-90 dark:text-gray-600" />
              <span className="text-sm">Search : </span>
              <div className="ml-1 relative inline-block">
                <select
                  value={selectedSearchId || ''}
                  onChange={(e) => dispatch(updateSelectedSearch(e.target.value || null))}
                  title={selectedSearch ? searchLabel(selectedSearch) : 'All Searches'}
                  className="
                    h-8 w-56 pl-3 pr-8
                    text-xs font-medium
                    rounded-full
                    bg-white text-gray-700
                    border border-gray-300
                    shadow-sm
                    cursor-pointer
                    transition-all duration-150
                    appearance-none truncate
                    focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500
                    hover:border-gray-400
                    dark:bg-gray-900 dark:text-gray-200
                    dark:border-gray-700 dark:hover:border-gray-600
                  "
                >
                  <option value="" className="bg-white text-gray-700 dark:bg-gray-900 dark:text-gray-200">
                    All Searches
                  </option>
                  {/* Arriving on a search the options haven't loaded for yet: without a
                      matching option the control would read "All Searches" while the list
                      below was still filtered. */}
                  {selectedSearchId && !selectedSearch && (
                    <option value={selectedSearchId} className="bg-white text-gray-700 dark:bg-gray-900 dark:text-gray-200">
                      Selected search
                    </option>
                  )}
                  {searches.map((s) => (
                    <option
                      key={s.search_id}
                      value={s.search_id}
                      className="bg-white text-gray-700 dark:bg-gray-900 dark:text-gray-200"
                    >
                      {searchLabel(s)}
                    </option>
                  ))}
                </select>

                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                  <ChevronDown className="w-4 h-4 text-textPrimary dark:text-gray-300" />
                </span>
              </div>

              {/* How many the provider matched against the page we hold, and the way to more. */}
              {selectedSearch?.total_available > 0 && (
                <div className="ml-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    {selectedSearch.result_count} of {selectedSearch.total_available.toLocaleString()} matches
                  </span>
                  {selectedSearch.next_cursor && (
                    <button
                      type="button"
                      onClick={runLoadMore}
                      disabled={loadingMore}
                      className="text-purple-600 dark:text-purple-300 hover:text-purple-800 font-medium disabled:opacity-50"
                    >
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Status refinement — only Replied has a verdict to filter on. */}
          {statusOptions.length > 0 && (
            <div className="flex flex-row items-center justify-start">
              <Minus className="text-gray-300 rotate-90 dark:text-gray-600" />
              <span className="text-sm">Status : </span>
              <div className="ml-1 relative inline-block">
                <select
                  value={activeSubSubTab}
                  onChange={(e) => dispatch(updateActiveSubSubTab(e.target.value))}
                  className="
                    h-8 w-36 pl-3 pr-8
                    text-xs font-medium
                    rounded-full
                    bg-white text-gray-700
                    border border-gray-300
                    shadow-sm
                    cursor-pointer
                    transition-all duration-150
                    appearance-none
                    focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500
                    hover:border-gray-400
                    dark:bg-gray-900 dark:text-gray-200
                    dark:border-gray-700 dark:hover:border-gray-600
                  "
                >
                  {statusOptions.map((option) => (
                    <option
                      key={option}
                      value={option}
                      className="bg-white text-gray-700 dark:bg-gray-900 dark:text-gray-200"
                    >
                      {option}
                    </option>
                  ))}
                </select>

                {/* Custom dropdown arrow */}
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                  <ChevronDown className="w-4 h-4 text-textPrimary dark:text-gray-300" />
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              value={searchQuery}
              onChange={(e) => dispatch(updateSearchQuery(e.target.value))}
              placeholder="Search by name"
              // Chrome lumps every form-less field on the page into one autofill group,
              // so picking a contact suggestion in the side panel's email editor also
              // filled this box (its placeholder reads as an email field) and set the
              // filter. Opt it out of autofill so only typed input lands here.
              name="candidate-search"
              autoComplete="off"
              className="w-60 text-sm bg-transparent border border-gray-300 dark:border-gray-600 rounded-full pl-9 pr-3 py-2 outline-none focus:border-purple-400 text-textPrimary dark:text-gray-200"
            />
          </div>
          {/* Both windows are properties of a sourced candidate; Outreach rows are keyed
              on their engagement instead, so the control is hidden there. */}
          {filtersAvailable && <FindCandidatesFilter />}
        </div>
      </div>

      {editFiltersOpen && editableSearch && (
        <FilterModal
          initialQuery={editableSearch.parsed_query}
          busy={editRunning}
          error={editError}
          notice={editNotice}
          onClose={() => {
            if (editRunning) return;
            setEditFiltersOpen(false);
            setEditNotice(null);
          }}
          onRunSearch={runEditedFilters}
        />
      )}
    </div>
  );
}

export default FindCandidatesHeader;
